import { useCallback, useEffect, useMemo, useState } from 'react';
import ProviderOrverview from './ProviderOrverview';
import ProviderReview from './ProviderReview';
import {
  useFetchAllPublicServiceByProvider,
  useFetchBusinessDetailsBySlug,
  useFetchReviewOverall,
} from '../../../hook/useQueryData';
import { Pagination } from '@heroui/react';
import PortFolio from './PortFolio';
import ProviderCertificate from './ProviderCertificate';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getProviderPublicStats } from '../../../service/bookingService';
import { IServiceQuery } from '../../../utils/type';
import FulLoading from '../../components/common/ui/loading/FullLoading';
import { ERROR_CODE } from '../../../utils/enum';
import {
  Seo,
  buildPersonSchema,
  buildWebPageSchema,
  getBaseUrl,
} from '../../../core/seo';
import ProviderHero from './components/ProviderHero';
import ProviderProfileTabs from './components/ProviderProfileTabs';
import { PROVIDER_SERVICES_ANCHOR_ID } from './components/constants';

const ProviderProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // @prefixed slugs are business handles — redirect to BusinessDetail
  useEffect(() => {
    if (slug?.startsWith('@')) {
      navigate(`/business/${slug}`, { replace: true });
    }
  }, [slug, navigate]);

  const { data: providerDetails, isFetching: isFetchingProvider } =
    useFetchBusinessDetailsBySlug(slug ?? '');

  useEffect(() => {
    if (isFetchingProvider) return;

    // providerDetails is now a Business object; check isActive flag directly
    if (!providerDetails?.isActive) {
      navigate(
        `/error?origin=app&code=${ERROR_CODE.RESOURCE_NO_LONGER_AVAILABLE}`
      );
      return;
    }
  }, [providerDetails, isFetchingProvider]);

  const query: IServiceQuery = {
    fullProfile: false,
    filters: {
      businessId: providerDetails?.businessId,
      isActive: true,
    },
    isDeleted: false,
    isVisible: true,
    serviceProjection:
      'serviceId,serviceTitle,categoryId,slug,price,providerId,isDiscount,isPackage,bookingType',
    galleryProjection: 'serviceImages',
    categoryProjection: 'categoryName',
    locationProjection: 'city,state',
    withMongoId: false,
    page: currentPage,
    limit: 15,
  };

  const { data: serviceData, isLoading: isAllServiceGetLoading } =
    useFetchAllPublicServiceByProvider({ data: query });

  const { data: apiReviewOverall, isPending: isLoadingStart } =
    useFetchReviewOverall(providerDetails?.providerId);

  // Public provider stats (completed jobs).
  const { data: bookingAnalytics } = useQuery({
    queryKey: ['providerPublicStats', providerDetails?.providerId],
    queryFn: () => getProviderPublicStats(providerDetails?.providerId || ''),
    enabled: !!providerDetails?.providerId,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const services = useMemo(
    () => serviceData?.services || [],
    [serviceData, isAllServiceGetLoading]
  );

  const overallRating = apiReviewOverall?.rating;

  const totalPage = useMemo(() => serviceData?.totalPages || 1, [serviceData]);

  const totalServices = useMemo(() => {
    return serviceData?.total ?? serviceData?.services?.length;
  }, [serviceData]);

  /**
   * Completed jobs from the public stats endpoint. Stays `undefined` when the
   * endpoint is unavailable so the stat tile hides instead of showing a zero.
   */
  const completedJobs = useMemo<number | undefined>(() => {
    const raw =
      bookingAnalytics?.analytics?.completedCount ??
      bookingAnalytics?.completedCount;
    return typeof raw === 'number' ? raw : undefined;
  }, [bookingAnalytics]);

  const hasServices = (services?.length ?? 0) > 0;

  const handleHireNow = useCallback(() => {
    setActiveTab('overview');
    // Defer so the overview panel is mounted before scrolling to it.
    requestAnimationFrame(() => {
      document
        .getElementById(PROVIDER_SERVICES_ANCHOR_ID)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const tabs = [
    { id: 'overview', title: 'Overview' },
    { id: 'portfolio', title: 'Portfolio' },
    { id: 'certificate', title: 'Certificate' },
    { id: 'reviews', title: 'Reviews & Ratings' },
  ];

  if (isFetchingProvider) {
    return <FulLoading />;
  }

  const providerName = providerDetails?.name || 'Provider';
  const providerBio = providerDetails?.bio || `${providerName} on HirePanther`;
  const providerImage = providerDetails?.profileImage || '';
  const baseUrl = getBaseUrl();
  const providerUrl = `${baseUrl}/hp/${slug}`;
  const providerJobTitle = providerDetails?.tagLine || 'Service Provider';

  const personSchema = buildPersonSchema({
    name: providerName,
    description: providerBio,
    url: providerUrl,
    image: providerImage,
    jobTitle: providerJobTitle,
    address: providerDetails?.location
      ? {
          city: providerDetails.location.city || '',
          state: providerDetails.location.state || '',
          country: 'CA',
        }
      : undefined,
  });

  const webPageSchema = buildWebPageSchema({
    name: `${providerName} | HirePanther`,
    description: providerBio,
    url: providerUrl,
    mainEntity: personSchema,
  });

  return (
    <>
      <Seo
        title={providerName}
        description={providerBio}
        canonicalPath={`/hp/${slug}`}
        ogImage={providerImage || undefined}
        structuredData={[personSchema, webPageSchema]}
      />

      <div className="min-h-screen bg-[#F6F8F9] pb-12">
        <ProviderHero
          businessDetails={providerDetails}
          averageRating={overallRating?.averageRating}
          totalReviews={overallRating?.totalReviews}
          isRatingLoading={isLoadingStart}
          completedJobs={completedJobs}
          totalServices={totalServices}
          onHireNow={hasServices ? handleHireNow : undefined}
        />

        <div className="mx-auto w-full max-w-screen-2xl px-3 sm:px-4">
          <ProviderProfileTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabs={tabs}
          />

          {activeTab === 'overview' && (
            <div
              role="tabpanel"
              id="provider-tabpanel-overview"
              aria-labelledby="provider-tab-overview"
            >
              <ProviderOrverview
                providerData={providerDetails}
                service={services || []}
                isProviderLoading={isFetchingProvider}
                isServiceLoading={isAllServiceGetLoading}
              />
              {totalPage > 1 && (
                <div className="flex items-end justify-end">
                  <Pagination
                    className="mt-10 pl-5"
                    showControls
                    size="sm"
                    page={currentPage}
                    total={totalPage}
                    onChange={setCurrentPage}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'portfolio' && (
            <div
              role="tabpanel"
              id="provider-tabpanel-portfolio"
              aria-labelledby="provider-tab-portfolio"
            >
              <PortFolio id={providerDetails?.providerId} />
            </div>
          )}

          {activeTab === 'certificate' && (
            <div
              role="tabpanel"
              id="provider-tabpanel-certificate"
              aria-labelledby="provider-tab-certificate"
            >
              <ProviderCertificate id={providerDetails?.providerId} />
            </div>
          )}

          {activeTab === 'reviews' && (
            <div
              role="tabpanel"
              id="provider-tabpanel-reviews"
              aria-labelledby="provider-tab-reviews"
            >
              <ProviderReview id={providerDetails?.providerId} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProviderProfile;
